import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, BarChart3, MessageSquare, LogOut, Settings, Globe, Smartphone, User, Clock, TrendingUp, Eye, Users, ArrowUpRight, Monitor, Layers } from 'lucide-react';

const API_URL = '/.netlify/functions';

const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchStats();
      fetchSubmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('adminToken', data.token);
        setToken(data.token);
      } else {
        alert(data.error);
      }
    } catch (err) {
      alert('Login failed');
    }
    setLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setStats(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setSubmissions(data);
    } catch (err) {
      console.error(err);
    }
  };

  const changePassword = async (newPassword) => {
    try {
      const res = await fetch(`${API_URL}/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword })
      });
      if (res.ok) alert('Password updated!');
    } catch (err) {
      alert('Failed to update password');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  // --- LOGIN SCREEN ---
  if (!token) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_black] p-8 max-w-md w-full"
        >
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-[#FF6B6B] border-2 border-black rounded-full">
              <Lock size={32} className="text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-black text-center mb-6 uppercase">Admin Access</h2>
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block font-bold text-sm mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-gray-50 border-2 border-black p-3 rounded font-medium focus:outline-none focus:shadow-[4px_4px_0px_0px_#8C52FF]"
                required
              />
            </div>
            <div>
              <label className="block font-bold text-sm mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-gray-50 border-2 border-black p-3 rounded font-medium focus:outline-none focus:shadow-[4px_4px_0px_0px_#8C52FF]"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 font-bold uppercase tracking-wider hover:bg-[#8C52FF] transition-colors"
            >
              {loading ? ' unlocking...' : 'Enter Dashboard'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // --- DASHBOARD ---
  return (
    <div className="min-h-screen bg-[#F0F4F8] flex flex-col">
      {/* Top Bar */}
      <div className="bg-white border-b-2 border-black py-4 px-6 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FFD233] border-2 border-black rounded flex items-center justify-center font-black">N</div>
          <span className="font-black text-xl uppercase">Admin Panel</span>
        </div>
        <button onClick={logout} className="flex items-center gap-2 font-bold text-sm hover:text-red-600">
          <LogOut size={16} /> Logout
        </button>
      </div>

      <div className="flex flex-1 flex-col md:flex-row">
        {/* Sidebar */}
        <div className="w-full md:w-64 bg-white border-b-2 md:border-b-0 md:border-r-2 border-black p-4 flex flex-row md:flex-col gap-2 overflow-x-auto">
          {[
            { id: 'stats', label: 'Analytics', icon: <BarChart3 size={20} /> },
            { id: 'submissions', label: 'Form Data', icon: <MessageSquare size={20} /> },
            { id: 'settings', label: 'Settings', icon: <Settings size={20} /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-4 py-3 font-bold rounded-lg transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-[#A0E7E5] border-2 border-black shadow-[4px_4px_0px_0px_black]' : 'hover:bg-gray-100'}`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-6 md:p-10 overflow-y-auto">

          {activeTab === 'stats' && stats && (
            <div className="space-y-8 animate-fadeIn">
              <h2 className="text-3xl font-black uppercase mb-6">Analytics Dashboard</h2>

              {/* ===== OVERVIEW CARDS ===== */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                  className="bg-white p-5 border-2 border-black shadow-[4px_4px_0px_0px_black]">
                  <div className="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase mb-2"><Eye size={14} /> Total Visits</div>
                  <div className="text-4xl font-black">{stats.totalVisits}</div>
                  <div className="text-xs text-gray-400 mt-1 font-bold">Today: {stats.visitsToday}</div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="bg-white p-5 border-2 border-black shadow-[4px_4px_0px_0px_#8C52FF]">
                  <div className="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase mb-2"><Users size={14} /> Unique Visitors</div>
                  <div className="text-4xl font-black text-[#8C52FF]">{stats.uniqueVisitors}</div>
                  <div className="text-xs text-gray-400 mt-1 font-bold">Sessions: {stats.totalSessions}</div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                  className="bg-white p-5 border-2 border-black shadow-[4px_4px_0px_0px_#FF6B6B]">
                  <div className="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase mb-2"><ArrowUpRight size={14} /> Bounce Rate</div>
                  <div className="text-4xl font-black text-[#FF6B6B]">{stats.bounceRate}%</div>
                  <div className="text-xs text-gray-400 mt-1 font-bold">{stats.bounceSessions} bounced</div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                  className="bg-white p-5 border-2 border-black shadow-[4px_4px_0px_0px_#FFD233]">
                  <div className="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase mb-2"><TrendingUp size={14} /> Returning</div>
                  <div className="text-4xl font-black text-[#FFD233]">{stats.returningPct}%</div>
                  <div className="text-xs text-gray-400 mt-1 font-bold">Come back again</div>
                </motion.div>
              </div>

              {/* ===== SESSION METRICS ===== */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-5 border-2 border-black shadow-[4px_4px_0px_0px_#A0E7E5] flex items-center gap-4">
                  <div className="p-3 bg-[#A0E7E5] border-2 border-black rounded-lg"><Clock size={24} /></div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase">Avg Session</div>
                    <div className="text-2xl font-black">{formatDuration(stats.avgDuration)}</div>
                  </div>
                </div>
                <div className="bg-white p-5 border-2 border-black shadow-[4px_4px_0px_0px_#8C52FF] flex items-center gap-4">
                  <div className="p-3 bg-[#E0E7FF] border-2 border-black rounded-lg"><TrendingUp size={24} /></div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase">Longest Session</div>
                    <div className="text-2xl font-black">{formatDuration(stats.maxDuration)}</div>
                  </div>
                </div>
                <div className="bg-white p-5 border-2 border-black shadow-[4px_4px_0px_0px_#FF6B6B] flex items-center gap-4">
                  <div className="p-3 bg-[#FFE0E0] border-2 border-black rounded-lg"><ArrowUpRight size={24} /></div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase">Bounce ({`<`}5s)</div>
                    <div className="text-2xl font-black">{stats.bounceShortSessions}</div>
                  </div>
                </div>
              </div>

              {/* ===== TRAFFIC SOURCES ===== */}
              <div className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_black] p-6 rounded-xl">
                <h3 className="font-black text-xl mb-5 flex items-center gap-2"><Globe size={20} /> Traffic Sources</h3>
                {stats.trafficSources && stats.trafficSources.length > 0 ? (
                  <div className="space-y-4">
                    {stats.trafficSources.map((src, i) => {
                      const colors = { 'Direct': '#8C52FF', 'Google': '#4285F4', 'Social': '#FF6B6B', 'Referral': '#FFD233', 'Paid Ads': '#A0E7E5' };
                      const pct = stats.totalVisits > 0 ? ((src.count / stats.totalVisits) * 100).toFixed(1) : 0;
                      return (
                        <div key={i}>
                          <div className="flex justify-between font-bold text-sm mb-1">
                            <span className="uppercase">{src.source}</span>
                            <span>{src.count} ({pct}%)</span>
                          </div>
                          <div className="h-4 bg-gray-100 rounded-full overflow-hidden border border-black">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.8, delay: i * 0.1 }}
                              className="h-full rounded-full"
                              style={{ backgroundColor: colors[src.source] || '#999' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 italic">No traffic data yet.</p>
                )}
              </div>

              {/* ===== PAGE ANALYTICS ===== */}
              <div className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_#8C52FF] p-6 rounded-xl">
                <h3 className="font-black text-xl mb-5 flex items-center gap-2"><Layers size={20} /> Page Analytics</h3>
                {stats.pageAnalytics && stats.pageAnalytics.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b-2 border-black">
                          <th className="p-3 font-black uppercase text-sm">Page</th>
                          <th className="p-3 font-black uppercase text-sm text-center">Visits</th>
                          <th className="p-3 font-black uppercase text-sm text-center">Unique</th>
                          <th className="p-3 font-black uppercase text-sm text-center">Avg Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.pageAnalytics.map((pg, i) => (
                          <tr key={i} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 font-bold">{pg.page || '/'}</td>
                            <td className="p-3 text-center"><span className="bg-[#E0E7FF] text-[#4F46E5] px-2 py-1 rounded font-bold text-xs">{pg.visits}</span></td>
                            <td className="p-3 text-center"><span className="bg-[#F0FFF4] text-[#22C55E] px-2 py-1 rounded font-bold text-xs">{pg.unique}</span></td>
                            <td className="p-3 text-center font-medium text-gray-600">{formatDuration(Math.round(pg.avgDuration || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-400 italic">No page data yet.</p>
                )}
              </div>

              {/* ===== DEVICE / BROWSER / OS ===== */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Device */}
                <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#FFD233] p-6 rounded-xl">
                  <h3 className="font-black text-lg mb-4 flex items-center gap-2"><Smartphone size={18} /> Devices</h3>
                  <div className="space-y-3">
                    {stats.deviceStats && stats.deviceStats.map((d, i) => (
                      <div key={i}>
                        <div className="flex justify-between font-bold text-sm mb-1 uppercase">
                          <span>{d._id || 'Unknown'}</span>
                          <span>{d.count}</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-black">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(d.count / stats.totalVisits) * 100}%` }}
                            className="h-full bg-[#FFD233] rounded-full"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Browsers */}
                <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#8C52FF] p-6 rounded-xl">
                  <h3 className="font-black text-lg mb-4 flex items-center gap-2"><Monitor size={18} /> Browsers</h3>
                  <div className="space-y-3">
                    {stats.browserStats && stats.browserStats.map((b, i) => (
                      <div key={i}>
                        <div className="flex justify-between font-bold text-sm mb-1">
                          <span>{b._id}</span>
                          <span>{b.count}</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-black">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(b.count / stats.totalVisits) * 100}%` }}
                            className="h-full bg-[#8C52FF] rounded-full"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* OS */}
                <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#A0E7E5] p-6 rounded-xl">
                  <h3 className="font-black text-lg mb-4 flex items-center gap-2"><Layers size={18} /> Operating Systems</h3>
                  <div className="space-y-3">
                    {stats.osStats && stats.osStats.map((o, i) => (
                      <div key={i}>
                        <div className="flex justify-between font-bold text-sm mb-1">
                          <span>{o._id}</span>
                          <span>{o.count}</span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-black">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(o.count / stats.totalVisits) * 100}%` }}
                            className="h-full bg-[#A0E7E5] rounded-full"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ===== RECENT VISITORS ===== */}
              <div className="bg-white border-2 border-black shadow-[8px_8px_0px_0px_black] p-6 rounded-xl">
                <h3 className="font-black text-xl mb-4 flex items-center gap-2"><Globe size={20} /> Recent Visitors</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b-2 border-black">
                        <th className="p-3 font-black uppercase text-sm">Time</th>
                        <th className="p-3 font-black uppercase text-sm">Page</th>
                        <th className="p-3 font-black uppercase text-sm">Location</th>
                        <th className="p-3 font-black uppercase text-sm">Device</th>
                        <th className="p-3 font-black uppercase text-sm">Browser</th>
                        <th className="p-3 font-black uppercase text-sm">OS</th>
                        <th className="p-3 font-black uppercase text-sm">Duration</th>
                        <th className="p-3 font-black uppercase text-sm">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentVisits.map((v, i) => {
                        const source = v.utmSource || (v.referrer ? new URL(v.referrer).hostname : 'Direct');
                        return (
                          <tr key={i} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 font-medium text-sm text-gray-600 whitespace-nowrap">{new Date(v.timestamp).toLocaleString()}</td>
                            <td className="p-3 font-bold text-sm">{v.page || '/'}</td>
                            <td className="p-3 font-bold text-sm">{v.city}, {v.country}</td>
                            <td className="p-3"><span className="bg-[#FFF3CD] text-[#856404] px-2 py-1 rounded font-bold uppercase text-xs">{v.device}</span></td>
                            <td className="p-3 text-sm font-medium">{v.browser || '-'}</td>
                            <td className="p-3 text-sm font-medium">{v.os || '-'}</td>
                            <td className="p-3 text-sm font-bold">{formatDuration(v.duration || 0)}</td>
                            <td className="p-3"><span className="bg-[#E0E7FF] text-[#4F46E5] px-2 py-1 rounded font-bold text-xs">{source}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          )}

          {activeTab === 'stats' && !stats && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin w-10 h-10 border-4 border-black border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="font-bold text-gray-500 uppercase">Loading Analytics...</p>
              </div>
            </div>
          )}

          {activeTab === 'submissions' && (
            <div className="animate-fadeIn">
              <h2 className="text-3xl font-black uppercase mb-6">Form Submissions</h2>
              <div className="grid gap-4">
                {submissions.map((sub, i) => (
                  <div key={i} className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_black] hover:shadow-[2px_2px_0px_0px_black] transition-all">
                    <div className="flex flex-col md:flex-row justify-between mb-4 border-b-2 border-gray-100 pb-2">
                      <h3 className="font-black text-lg">{sub.name}</h3>
                      <span className="text-sm font-medium text-gray-500">{new Date(sub.date).toLocaleString()}</span>
                    </div>
                    <div className="space-y-2">
                      <p className="font-bold flex items-center gap-2"><User size={16} /> {sub.email}</p>
                      <p className="font-bold text-[#FF6B6B] uppercase text-xs tracking-wider">{sub.service}</p>
                      <p className="text-gray-700 bg-gray-50 p-3 border-2 border-gray-200 rounded mt-2">"{sub.message}"</p>
                    </div>
                  </div>
                ))}
                {submissions.length === 0 && <p className="text-gray-500 italic">No submissions yet.</p>}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="max-w-md animate-fadeIn">
              <h2 className="text-3xl font-black uppercase mb-6">Admin Settings</h2>
              <div className="bg-white border-2 border-black p-8 shadow-[8px_8px_0px_0px_black]">
                <h3 className="font-bold text-lg mb-4">Change Password</h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  changePassword(e.target.newPass.value);
                  e.target.reset();
                }} className="space-y-4">
                  <input type="password" name="newPass" placeholder="New Password" className="w-full bg-[#F0F4F8] border-2 border-black p-3 rounded font-bold" required />
                  <button className="bg-[#FFD233] text-black w-full py-3 font-black uppercase border-2 border-black shadow-[4px_4px_0px_0px_black] active:shadow-none hover:translate-x-[2px] transition-all">Update Security</button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
